import * as yup from "yup";

import {
    AssessmentSchedule,
    DailyRecurrence,
    MonthlyRecurrence,
    Recurrence,
    WeeklyRecurrence
} from "../../models";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {
    DayOfWeek,
    MonthlyScheduleType,
    RecurrenceType
} from "../../API";
import {
    format,
    parse,
    startOfToday
} from "date-fns";
import {
    useMemo,
    useRef,
    useState
} from "react";

import ClearIcon from "@mui/icons-material/Clear";
import { DailyRecurrenceForm } from "./DailyRecurrenceForm";
import { MonthlyRecurrenceForm } from "./MonthlyReccurenceForm";
import { WeeklyRecurrenceForm } from "./WeeklyReccurenceForm";
import _ from "lodash";
import { assessmentScheduleSchema } from "./schema";
import { scheduleToHumanReadableConverter } from "./scheduleToHumanReadableConverter";

// Make certain properties optional
export type Optional<T, K extends keyof T> = Omit<T, K> & { [P in K]?: T[P] };

const DEFAULT_DAILY_RECURRENCE: DailyRecurrence = {
    interval: 1
};
const DEFAULT_WEEKLY_RECURRENCE: WeeklyRecurrence = {
    interval: 1,
    daysOfWeek: []
};
const DEFAULT_MONTHLY_RECURRENCE: MonthlyRecurrence = {
    type: MonthlyScheduleType.DAY_OF_MONTH,
    interval: 1,
    dayOfMonth: startOfToday().getDate().toString(),
    nthWeekDay: {
        weekNumber: "1",
        day: DayOfWeek.MONDAY
    }
};

const DEFAULT_RECURRENCE: Recurrence = {
    type: RecurrenceType.DAILY,
    dailyRecurrence: DEFAULT_DAILY_RECURRENCE
};

const DEFAULT_ASSESSMENT_SCHEDULE: Partial<AssessmentSchedule> = {
    startDate: startOfToday().getTime(),
    recurrence: { ...DEFAULT_RECURRENCE }
};

type AssessmentScheduleFormProps = {
    initialAssessmentSchedule?: AssessmentSchedule;
    onCancel?: () => void;
    onSubmit: (assessmentSchedule: AssessmentSchedule) => (Promise<void> | void);
    cancelLabel?: string;
    submitLabel?: string;
};
export const AssessmentScheduleRecurrenceForm = (props: AssessmentScheduleFormProps) => {
    const { initialAssessmentSchedule, onSubmit, onCancel, cancelLabel = "Cancel", submitLabel = "Submit" } = props;
    const [assessmentScheduleWithoutRecurrence, setAssessmentScheduleWithoutRecurrence] = useState<Optional<AssessmentSchedule, "id">>(initialAssessmentSchedule ?? DEFAULT_ASSESSMENT_SCHEDULE);
    const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(initialAssessmentSchedule?.recurrence?.type as RecurrenceType ?? DEFAULT_RECURRENCE.type);
    const [dailyRecurrence, setDailyRecurrence] = useState<DailyRecurrence>(initialAssessmentSchedule?.recurrence?.dailyRecurrence ?? DEFAULT_DAILY_RECURRENCE);
    const [weeklyRecurrence, setWeeklyRecurrence] = useState<WeeklyRecurrence>(initialAssessmentSchedule?.recurrence?.weeklyRecurrence ?? DEFAULT_WEEKLY_RECURRENCE);
    const [monthlyRecurrence, setMonthlyRecurrence] = useState<MonthlyRecurrence>(initialAssessmentSchedule?.recurrence?.monthlyRecurrence ?? DEFAULT_MONTHLY_RECURRENCE);
    const [showDayOfMonthWarning, setShowDayOfMonthWarning] = useState<boolean>(false);
    const resolveDayOfMonthWarningPromise = useRef<() => void>(() => { });

    // Validation
    const [validationError, setValidationError] = useState<Record<any, any>>({});
    const validationErrorRef = useRef(validationError);
    validationErrorRef.current = validationError;

    const recurrence = useMemo<Recurrence>(() => {
        switch (recurrenceType) {
            case RecurrenceType.DAILY:
                return {
                    type: recurrenceType,
                    dailyRecurrence
                };
            case RecurrenceType.WEEKLY:
                return {
                    type: recurrenceType,
                    weeklyRecurrence
                };
            case RecurrenceType.MONTHLY:
                return {
                    type: recurrenceType,
                    monthlyRecurrence
                };
            default:
                return {
                    type: recurrenceType
                };
        }
    }, [recurrenceType, dailyRecurrence, weeklyRecurrence, monthlyRecurrence]);

    const assessmentSchedule = useMemo<Optional<AssessmentSchedule, "id">>(() => {
        return {
            ...assessmentScheduleWithoutRecurrence,
            recurrence
        };
    }, [assessmentScheduleWithoutRecurrence, recurrence]);

    return (
        <>
            <Box
                component="form"
                onSubmit={async (e) => {
                    e.preventDefault();
                    setValidationError({});
                    if (
                        assessmentSchedule.recurrence?.type === RecurrenceType.MONTHLY &&
                        assessmentSchedule.recurrence.monthlyRecurrence?.type === MonthlyScheduleType.DAY_OF_MONTH &&
                        assessmentSchedule.recurrence.monthlyRecurrence.dayOfMonth &&
                        parseInt(assessmentSchedule.recurrence.monthlyRecurrence.dayOfMonth) > 28
                    ) {
                        setShowDayOfMonthWarning(true);
                        await new Promise<void>((resolve) => {
                            resolveDayOfMonthWarningPromise.current = resolve;
                        });
                    }
                    const result = await assessmentScheduleSchema
                        .validate(assessmentSchedule, { abortEarly: false })
                        .catch((error) => {
                            if (error instanceof yup.ValidationError) {
                                setValidationError(prev => ({
                                    ...prev,
                                    ...error.inner.reduce((acc: any, err) => {
                                        _.set(acc, err.path!, err.message);
                                        return acc;
                                    }, {})
                                }));
                                return;
                            }
                            throw error;
                        });
                    console.log(result);
                    if (Object.keys(validationErrorRef.current).length > 0) {
                        return;
                    }
                    await onSubmit(assessmentSchedule as AssessmentSchedule);
                }}
                noValidate
                display="flex"
                flexDirection="column"
                gap={2}
            >
                <Box display="grid" gridTemplateColumns="max-content 1fr" gap={2} alignItems="center">
                    <Typography>Name</Typography>
                    <Box>
                        <TextField
                            id="name"
                            name="name"
                            value={assessmentSchedule.name}
                            onChange={(e) => setAssessmentScheduleWithoutRecurrence(prev => ({ ...prev, name: e.target.value }))}
                            size="small"
                            autoComplete="off"
                            error={!!validationError.name}
                            helperText={validationError.name}
                            inputProps={{ maxLength: 50 }}
                            multiline
                            maxRows={3}
                        />
                    </Box>
                    <Typography>Start</Typography>
                    <Box>
                        <TextField
                            id="startDate"
                            name="startDate"
                            type="date"
                            value={assessmentSchedule.startDate ? format(new Date(assessmentSchedule.startDate), "yyyy-MM-dd") : ""}
                            onChange={(e) => {
                                const startDate = e.target.value ? parse(e.target.value, "yyyy-MM-dd", new Date()).getTime() : undefined;
                                if (startDate) {
                                    setMonthlyRecurrence(prev => ({
                                        ...prev,
                                        dayOfMonth: new Date(startDate).getDate().toString()
                                    }));
                                }
                                setAssessmentScheduleWithoutRecurrence(prev => ({ ...prev, startDate }));
                            }}
                            variant="standard"
                            size="small"
                            error={!!validationError.startDate}
                            helperText={validationError.startDate}
                        />
                    </Box>
                    <Typography>Type</Typography>
                    <Box>
                        <FormControl size="small" variant="standard">
                            <Select
                                id="linkAction"
                                labelId="linkAction-label"
                                name="linkAction"
                                value={assessmentSchedule.recurrence?.type}
                                onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
                                variant="standard"
                            >
                                {Object.values(RecurrenceType).map((type) => (
                                    <MenuItem key={type} value={type}>{_.capitalize(type)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {assessmentSchedule.recurrence?.type === RecurrenceType.DAILY && (
                        <DailyRecurrenceForm
                            dailyRecurrence={dailyRecurrence}
                            setDailyRecurrence={setDailyRecurrence}
                            error={validationError?.recurrence?.dailyRecurrence as any}
                        />
                    )}
                    {assessmentSchedule.recurrence?.type === RecurrenceType.WEEKLY && (
                        <WeeklyRecurrenceForm
                            weeklyRecurrence={weeklyRecurrence}
                            setWeeklyRecurrence={setWeeklyRecurrence}
                            error={validationError?.recurrence?.weeklyRecurrence as any}
                        />
                    )}
                    {assessmentSchedule.recurrence?.type === RecurrenceType.MONTHLY && (
                        <MonthlyRecurrenceForm
                            monthlyRecurrence={monthlyRecurrence}
                            setMonthlyRecurrence={setMonthlyRecurrence}
                            error={validationError?.recurrence?.monthlyRecurrence as any}
                        />
                    )}
                    <Box display="flex" gap={0.5} alignItems="center">
                        <Typography>End</Typography>
                        <Typography variant="caption" color="GrayText">(Opt.)</Typography>
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                        <TextField
                            id="endDate"
                            name="endDate"
                            type="date"
                            value={assessmentSchedule.endDate ? format(new Date(assessmentSchedule.endDate), "yyyy-MM-dd") : ""}
                            onChange={(e) => {
                                const endDate = e.target.value ? parse(e.target.value, "yyyy-MM-dd", new Date()).getTime() : undefined;
                                setAssessmentScheduleWithoutRecurrence(prev => ({ ...prev, endDate }));
                            }}
                            variant="standard"
                            size="small"
                            error={!!validationError.endDate}
                            helperText={validationError.endDate}
                        />
                        {!!assessmentSchedule.endDate && (
                            <IconButton
                                onClick={() => setAssessmentScheduleWithoutRecurrence(prev => ({ ...prev, endDate: undefined }))}
                                size="small"
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
                <Box display="flex" flexWrap="wrap">
                    <Typography variant="body1" fontWeight={500}>{scheduleToHumanReadableConverter.convert(assessmentSchedule)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-around">
                    <Button
                        onClick={onCancel}
                        color="error"
                        variant="contained"
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                    >
                        {submitLabel}
                    </Button>
                </Box>
            </Box>
            <Dialog
                open={showDayOfMonthWarning}
                onClose={() => setShowDayOfMonthWarning(false)}
            >
                <DialogTitle>
                    Some months have fewer than {monthlyRecurrence.dayOfMonth} days.
                </DialogTitle>
                <DialogContent>
                    For these months, the last day of the month will be used.
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowDayOfMonthWarning(false);
                            resolveDayOfMonthWarningPromise.current();
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
